import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Bar as BarChart } from 'react-chartjs-2';
import {
  Activity, AlertTriangle, ArrowRight, Building2, CheckCircle2, Clock, Gauge, HardDrive, Layers, Radar, Sparkles, UserRound, Users,
} from 'lucide-react';
import { baseOptions } from '../lib/charts';
import { useApi } from '../lib/useApi';
import { actionLabel, ago, bytes, num } from '../lib/format';
import { useAuth } from '../context/AuthContext';
import { Card, Chip, Empty, ErrorBanner, PageHeader, PageSkeleton, PLAN_TONE, Stat, TenantLogo, Tone } from '../components/ui';

interface OverviewData {
  kpis: Record<string, number> & { last_scan_at: string | null };
  indicator_total: number;
  storage: { total_bytes: number; database_bytes: number; file_bytes: number; files: number;
             top: { id: string; name: string; total_bytes: number; file_bytes: number }[] };
  signups_by_month: { label: string; tenants: number }[];
  scans_by_day: { label: string; scans: number }[];
  plans: { label: string; tenants: number }[];
  attention: { tenant_id: string; tenant: string; domain: string | null; logo_url: string | null; kind: string; severity: 'high' | 'medium' | 'low'; text: string; plan: string }[];
  attention_total: number;
  top_tenants: { id: string; name: string; domain: string | null; logo_url: string | null; plan: string; scans_30d: number; vendors: number }[];
  recent: { action: string; details: string; created_at: string; tenant: string | null; tenant_id: string | null; actor: string | null }[];
  sources?: { all: Record<string, number>; last_30d: Record<string, number> };
  portal?: { accounts: number; new_30d: number; rated: number; claimed: number };
}

const SOURCES: { key: string; label: string; color: string }[] = [
  { key: 'self', label: 'Self sign-up', color: '#94a3b8' }, { key: 'vendor_portal', label: 'Vendor portal', color: '#8b5cf6' },
  { key: 'admin', label: 'Created by team', color: '#3b82f6' },
];

const SEV_TONE: Record<string, Tone> = { high: 'red', medium: 'amber', low: 'slate' };
const PLAN_BAR: Record<string, string> = { Tropos: '#94a3b8', Strato: '#3b82f6', Meso: '#f59e0b', Thermo: '#f97316', Exo: '#8b5cf6', Custom: '#14b8a6' };

export default function Overview() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data, error, loading, reload } = useApi<OverviewData>('/overview', 60000);

  if (loading) return <PageSkeleton />;
  if (error || !data) return <ErrorBanner text={error || 'Could not load the overview'} onRetry={reload} />;
  const k = data.kpis;
  const totalPlan = data.plans.reduce((s, p) => s + p.tenants, 0) || 1;
  const hour = new Date().getHours();

  return (
    <div>
      <PageHeader
        title={`Good ${hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening'}, ${user?.name || 'admin'}`}
        subtitle={<>{num(k.tenants)} tenants on the platform · last scan {ago(k.last_scan_at)} · scans run {num(data.indicator_total)} checks each</>}
      />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Stat label="Active tenants" value={num(k.active_tenants)} icon={Building2} tone="blue"
          sub={`${num(k.new_tenants_30d)} new in 30 days · ${num(k.tenants - k.active_tenants)} deactivated`} onClick={() => navigate('/tenants')} />
        <Stat label="On trial" value={num(k.trials)} icon={Clock} tone={k.trials_ending || k.trials_expired ? 'amber' : 'teal'}
          sub={k.trials_expired ? `${num(k.trials_expired)} expired · ${num(k.trials_ending)} ending this week` : `${num(k.trials_ending)} ending this week`}
          onClick={() => navigate('/tenants?status=trial')} />
        <Stat label="Users" value={num(k.users)} icon={Users} tone="purple" sub={`${num(k.users_active_7d)} signed in this week`} />
        <Stat label="Scans" value={num(k.scans_30d)} icon={Radar} tone="green" sub={`last 30 days · ${num(k.scans_24h)} in 24 h · ${num(k.vendors)} vendors`} />
        <Stat label="Storage used" value={bytes(data.storage.total_bytes)} icon={HardDrive} tone="orange"
          sub={data.storage.top[0] ? `Largest: ${data.storage.top[0].name} (${bytes(data.storage.top[0].total_bytes)})` : `${num(data.storage.files)} files`}
          onClick={() => navigate('/tenants')} />
      </div>

      <div className="mt-5 grid gap-5 xl:grid-cols-[1.4fr_1fr]">
        <Card title="Needs attention" icon={AlertTriangle} tone="red"
          right={data.attention_total > 0 ? <Chip tone="red">{data.attention_total}</Chip> : null} bodyClass="p-0">
          {data.attention.length === 0 ? (
            <Empty icon={CheckCircle2} title="Nothing needs you right now" text="No trials ending, no tenants at their limits and nobody dormant." />
          ) : (
            <ul className="divide-y divide-slate-100">
              {data.attention.map((a, i) => (
                <li key={i}>
                  <button onClick={() => navigate(`/tenants/${a.tenant_id}`)} className="flex w-full items-center gap-3 px-5 py-3 text-left hover:bg-slate-50">
                    <TenantLogo name={a.tenant} domain={a.domain} logoUrl={a.logo_url} size={30} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-slate-900">{a.tenant}</span>
                      <span className="block text-xs text-slate-500">{a.text}</span>
                    </span>
                    <Chip tone={PLAN_TONE[a.plan] || 'slate'}>{a.plan}</Chip>
                    <Chip tone={SEV_TONE[a.severity]} dot>{a.severity === 'high' ? 'Act now' : a.severity === 'medium' ? 'Soon' : 'FYI'}</Chip>
                    <ArrowRight className="h-4 w-4 text-slate-300" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title="Tenants by plan" icon={Layers} tone="amber">
          {data.plans.length === 0 ? <Empty icon={Layers} title="No active tenants" /> : (
            <>
              <div className="flex h-3 overflow-hidden rounded-full">
                {data.plans.map(p => <div key={p.label} style={{ flex: p.tenants, background: PLAN_BAR[p.label] || '#94a3b8' }} title={`${p.label}: ${p.tenants}`} />)}
              </div>
              <ul className="mt-4 space-y-2.5">
                {data.plans.map(p => (
                  <li key={p.label} className="flex items-center gap-3 text-sm">
                    <span className="h-2.5 w-2.5 rounded-sm" style={{ background: PLAN_BAR[p.label] || '#94a3b8' }} />
                    <span className="flex-1 text-slate-700">{p.label}</span>
                    <span className="w-12 text-right text-xs text-slate-500">{Math.round(p.tenants / totalPlan * 100)}%</span>
                    <span className="w-8 text-right font-semibold text-slate-900">{p.tenants}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </Card>
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        <Card title="Where tenants come from" icon={Building2} tone="blue" right={<span className="text-xs text-slate-400">All time · last 30 days</span>}>
          {(() => {
            const all = data.sources?.all || {}; const recent = data.sources?.last_30d || {};
            const total = SOURCES.reduce((n, s) => n + (all[s.key] || 0), 0) || 1;
            return (
              <>
                <div className="flex h-3 overflow-hidden rounded-full bg-slate-100">
                  {SOURCES.filter(s => all[s.key]).map(s => <div key={s.key} style={{ flex: all[s.key], background: s.color }} title={`${s.label}: ${all[s.key]}`} />)}
                </div>
                <ul className="mt-4 space-y-2.5">
                  {SOURCES.map(s => (
                    <li key={s.key} className="flex items-center gap-3 text-sm">
                      <span className="h-2.5 w-2.5 rounded-sm" style={{ background: s.color }} />
                      <button onClick={() => navigate('/tenants')} className="flex-1 text-left text-slate-700 hover:text-slate-900">{s.label}</button>
                      <span className="w-20 text-right text-xs text-slate-500">{recent[s.key] ? `+${recent[s.key]} in 30d` : ''}</span>
                      <span className="w-12 text-right text-xs text-slate-500">{Math.round(((all[s.key] || 0) / total) * 100)}%</span>
                      <span className="w-8 text-right font-semibold text-slate-900">{all[s.key] || 0}</span>
                    </li>
                  ))}
                </ul>
              </>
            );
          })()}
        </Card>
        <Card title="Vendor portal" icon={UserRound} tone="purple" right={<button onClick={() => navigate('/portal')} className="text-xs font-medium text-blue-700 hover:underline">Open →</button>}>
          {(() => {
            const p = data.portal || { accounts: 0, new_30d: 0, rated: 0, claimed: 0 };
            const steps = [
              { label: 'Portal accounts', value: p.accounts, sub: `${num(p.new_30d)} new in 30 days` },
              { label: 'Shown their rating', value: p.rated, sub: 'company rated by FluxWatch' },
              { label: 'Claimed a workspace', value: p.claimed, sub: p.rated ? `${Math.round((p.claimed / p.rated) * 100)}% of those rated` : 'none yet' },
            ];
            const max = Math.max(1, p.accounts);
            return (
              <ul className="space-y-3.5">
                {steps.map(s => (
                  <li key={s.label}>
                    <div className="mb-1 flex items-baseline justify-between text-sm"><span className="text-slate-700">{s.label}</span>
                      <span><b className="font-semibold text-slate-900">{num(s.value)}</b> <span className="text-xs text-slate-500">{s.sub}</span></span></div>
                    <div className="h-2 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-violet-500" style={{ width: `${(s.value / max) * 100}%` }} /></div>
                  </li>
                ))}
              </ul>
            );
          })()}
        </Card>
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        <Card title="Scans per day" icon={Activity} tone="green" right={<span className="text-xs text-slate-400">Last 30 days</span>}>
          <div className="h-52">
            <BarChart data={{ labels: data.scans_by_day.map(d => d.label), datasets: [{ data: data.scans_by_day.map(d => d.scans), backgroundColor: '#10b981', borderRadius: 4, maxBarThickness: 18 }] }}
              options={baseOptions()} />
          </div>
        </Card>
        <Card title="New tenants per month" icon={Sparkles} tone="blue" right={<span className="text-xs text-slate-400">Last 12 months</span>}>
          <div className="h-52">
            <BarChart data={{ labels: data.signups_by_month.map(d => d.label), datasets: [{ data: data.signups_by_month.map(d => d.tenants), backgroundColor: '#3b82f6', borderRadius: 4, maxBarThickness: 26 }] }}
              options={baseOptions({ stepSize: 1 })} />
          </div>
        </Card>
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        <Card title="Most active tenants" icon={Gauge} tone="purple" right={<span className="text-xs text-slate-400">Scans in 30 days</span>} bodyClass="p-0">
          {data.top_tenants.length === 0 ? <Empty icon={Building2} title="No tenants yet" /> : (
            <ul className="divide-y divide-slate-100">
              {data.top_tenants.map(t => {
                const max = Math.max(...data.top_tenants.map(x => x.scans_30d), 1);
                return (
                  <li key={t.id}>
                    <button onClick={() => navigate(`/tenants/${t.id}`)} className="flex w-full items-center gap-3 px-5 py-3 text-left hover:bg-slate-50">
                      <TenantLogo name={t.name} domain={t.domain} logoUrl={t.logo_url} size={30} />
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2"><span className="truncate text-sm font-medium text-slate-900">{t.name}</span><Chip tone={PLAN_TONE[t.plan] || 'slate'}>{t.plan}</Chip></span>
                        <span className="mt-1.5 block h-1.5 overflow-hidden rounded-full bg-slate-100"><span className="block h-full rounded-full bg-violet-500" style={{ width: `${t.scans_30d / max * 100}%` }} /></span>
                      </span>
                      <span className="w-20 text-right text-xs text-slate-500"><b className="text-sm font-semibold text-slate-900">{num(t.scans_30d)}</b> scans<br />{num(t.vendors)} vendors</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
        <Card title="Recent platform actions" icon={Activity} tone="slate" right={<button onClick={() => navigate('/audit')} className="text-xs font-medium text-blue-700 hover:underline">Audit log →</button>} bodyClass="p-0">
          {data.recent.length === 0 ? <Empty icon={Activity} title="No platform actions yet" /> : (
            <ul className="divide-y divide-slate-100">
              {data.recent.map((r, i) => (
                <li key={i} className="flex items-start gap-3 px-5 py-2.5">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500" />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm text-slate-900">{actionLabel(r.action)}{r.tenant && <> · <button className="text-blue-700 hover:underline" onClick={() => navigate(`/tenants/${r.tenant_id}`)}>{r.tenant}</button></>}</span>
                    <span className="block truncate text-xs text-slate-500">{r.actor || 'System'} · {ago(r.created_at)}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
